// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { RemoteMarkdown } from '@/components/remote/RemoteMarkdown';
import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import type { AgentMessageItem } from '@web/lib/remoteWorkItemModel';
import { Check, Copy, ThumbsDown, ThumbsUp } from 'lucide';
import { useEffect, useRef, useState } from 'react';

const COPY_CONFIRMATION_MS = 2000;

export interface FinalAnswerBlockProps {
  item: AgentMessageItem;
  className?: string;
  onHelpful?: (item: AgentMessageItem) => void;
  onUnhelpful?: (item: AgentMessageItem) => void;
}

export function FinalAnswerBlock({
  item,
  className,
  onHelpful,
  onUnhelpful,
}: FinalAnswerBlockProps) {
  const content = item.text?.trim() ?? '';
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>(
    'idle'
  );
  const resetTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (resetTimerRef.current != null) {
        window.clearTimeout(resetTimerRef.current);
      }
    },
    []
  );

  const copyAnswer = async () => {
    try {
      if (!navigator.clipboard?.writeText)
        throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(content);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }

    if (resetTimerRef.current != null) {
      window.clearTimeout(resetTimerRef.current);
    }
    resetTimerRef.current = window.setTimeout(
      () => setCopyStatus('idle'),
      COPY_CONFIRMATION_MS
    );
  };

  return (
    <section
      className={cn('min-w-0 py-1', className)}
      aria-label="Agent final answer"
    >
      {content ? (
        <RemoteMarkdown content={content} />
      ) : (
        <p className="m-0 text-body-sm text-ds-text-neutral-muted-default">
          No final response was recorded.
        </p>
      )}

      {content && (
        <div className="mt-2 flex items-center gap-1">
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-ds-icon-neutral-muted-default hover:bg-ds-bg-neutral-subtle-hover hover:text-ds-icon-neutral-default-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring-brand-default-focus"
            aria-label={
              copyStatus === 'copied' ? 'Answer copied' : 'Copy answer'
            }
            onClick={copyAnswer}
          >
            {copyStatus === 'copied' ? (
              <MorphIcon icon={Check} aria-hidden className="h-4 w-4" />
            ) : (
              <MorphIcon icon={Copy} aria-hidden className="h-4 w-4" />
            )}
          </button>

          {onHelpful && (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-ds-icon-neutral-muted-default hover:bg-ds-bg-neutral-subtle-hover hover:text-ds-icon-neutral-default-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring-brand-default-focus"
              aria-label="Mark answer as helpful"
              onClick={() => onHelpful(item)}
            >
              <MorphIcon icon={ThumbsUp} aria-hidden className="h-4 w-4" />
            </button>
          )}

          {onUnhelpful && (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-ds-icon-neutral-muted-default hover:bg-ds-bg-neutral-subtle-hover hover:text-ds-icon-neutral-default-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring-brand-default-focus"
              aria-label="Mark answer as unhelpful"
              onClick={() => onUnhelpful(item)}
            >
              <MorphIcon icon={ThumbsDown} aria-hidden className="h-4 w-4" />
            </button>
          )}

          <span className="sr-only" role="status" aria-live="polite">
            {copyStatus === 'copied'
              ? 'Answer copied to clipboard.'
              : copyStatus === 'failed'
                ? 'The answer could not be copied.'
                : ''}
          </span>
        </div>
      )}
    </section>
  );
}
